import { collection, doc, getDoc, getDocs, runTransaction } from "firebase/firestore";
import { getNodeCustomerAccess } from "../../../utils/staffCustomerAccess";

export const ANNUAL_MASK_NODE_SENDERS_COLLECTION = "annualMaskNodeSenders";
export const normalizeSenderEmail = (value) => String(value || "").trim().toLowerCase();
export const isValidSenderEmail = (value) => /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(value);
const text = (value) => String(value || "").trim();
export const annualMaskSenderConfigRef = (database) => doc(database, ANNUAL_MASK_NODE_SENDERS_COLLECTION, "config");

// Older settings applied to both workflows. Preserve that selection until the
// first edit, which saves both lists together so subsequent edits are isolated.
export const getWorkflowSenders = (settings = {}, workflow = "annual-mask") =>
  workflow === "booking" && Array.isArray(settings.bookingSenders)
    ? settings.bookingSenders
    : settings.senders || [];

export const resolveAnnualMaskSenderNode = (caseItem = {}, nodes = []) => {
  const patient = caseItem.patient || {};
  const identifiers = [caseItem.nodeRef, caseItem.node, patient.nodeRef, patient.node].map(text).filter(Boolean);
  for (const identifier of identifiers) {
    const matches = nodes.filter((node) => [node.id, node.nodecode, node.nodename].some((value) => text(value) === identifier));
    if (matches.length > 1) throw new Error("More than one node matches this case. Check the node before sending email.");
    if (matches.length === 1) return matches[0];
  }
  return null;
};

export const validateAnnualMaskSenders = (senders) => {
  const emails = new Set();
  senders.forEach((sender, index) => {
    if (!isValidSenderEmail(sender.email)) throw new Error("Enter a valid sender email.");
    if (emails.has(sender.email)) throw new Error("This email is already configured. Edit its assignments instead.");
    emails.add(sender.email);
    if (!Array.isArray(sender.node) || !sender.node.length) throw new Error("Select at least one node.");
    sender.node.forEach((code) => {
      const scope = getNodeCustomerAccess(sender, code);
      if (scope.mode === "none") throw new Error(`Select customers for ${code}, or choose All customers.`);
      senders.slice(0, index).forEach((other) => {
        if (!other.node.includes(code)) return;
        const otherScope = getNodeCustomerAccess(other, code);
        if (scope.mode === "all" || otherScope.mode === "all" || scope.customerIds.some((id) => otherScope.customerIds.includes(id))) {
          throw new Error(`Sender assignments overlap for ${code}. Each customer can have only one sender.`);
        }
      });
    });
  });
};

// One transaction prevents concurrent editors from assigning the same customer
// to different sender emails.
export const saveAnnualMaskSenderProfile = async (database, originalEmail, profile, workflow = "annual-mask") => runTransaction(database, async (transaction) => {
  if (!["annual-mask", "booking"].includes(workflow)) throw new Error("Select a valid email workflow.");
  const reference = annualMaskSenderConfigRef(database);
  const snapshot = await transaction.get(reference);
  const settings = snapshot.exists() ? snapshot.data() : {};
  const current = getWorkflowSenders(settings, workflow);
  if (originalEmail && !current.some((sender) => sender.email === originalEmail)) throw new Error("This sender was removed. Reload the page before editing.");
  const next = current.filter((sender) => sender.email !== originalEmail);
  if (profile) next.push(profile);
  validateAnnualMaskSenders(next);
  next.sort((a, b) => a.email.localeCompare(b.email));
  transaction.set(reference, {
    ...settings,
    senders: getWorkflowSenders(settings),
    bookingSenders: getWorkflowSenders(settings, "booking"),
    [workflow === "booking" ? "bookingSenders" : "senders"]: next,
  });
  return next;
});

export const selectAnnualMaskSender = (caseItem, node, senders, customers = []) => {
  const patient = caseItem.patient || {};
  const customer = text(caseItem.refCustomer || caseItem.customer || patient.refCustomer || patient.customer);
  const matchingCustomers = customers.filter((row) => [row.id, row.CustomerNameTH, row.CustomerNameEN].some((value) => text(value) === customer));
  if (customer && matchingCustomers.length > 1) throw new Error("More than one customer matches this case. Check the customer before sending email.");
  const customerId = matchingCustomers.length ? matchingCustomers[0].id : customer;
  const matches = senders.filter((sender) => {
    if (!sender.node.includes(node.nodecode)) return false;
    const scope = getNodeCustomerAccess(sender, node.nodecode);
    return scope.mode === "all" || (customerId && scope.customerIds.includes(customerId));
  });
  if (matches.length > 1) throw new Error("Multiple sender emails match this case. Check Email Sender Settings.");
  if (!matches.length) return {};
  const email = normalizeSenderEmail(matches[0].email);
  if (!isValidSenderEmail(email)) throw new Error("The configured sender email is invalid. Check Email Sender Settings.");
  return { from: email };
};

export const loadAnnualMaskEmailSender = async (database, caseItem) => {
  const settings = await getDoc(annualMaskSenderConfigRef(database));
  const senders = getWorkflowSenders(settings.exists() ? settings.data() : {});
  if (!senders.length) return {};
  const nodes = await getDocs(collection(database, "nodes"));
  const node = resolveAnnualMaskSenderNode(caseItem, nodes.docs.map((row) => ({ ...row.data(), id: row.id })));
  if (!node) return {};
  const hasCustomerSelections = senders.some((sender) => sender.node.includes(node.nodecode) && getNodeCustomerAccess(sender, node.nodecode).mode === "selected");
  const customers = hasCustomerSelections ? await getDocs(collection(database, "nodes", node.id, "customers")) : null;
  return selectAnnualMaskSender(caseItem, node, senders, customers ? customers.docs.map((row) => ({ ...row.data(), id: row.id })) : []);
};
